import { Edges } from "@react-three/drei";
import * as THREE from "three";
import { useMemo } from "react";
import { fromMM } from "../Utils/frameUtils";
import { buildESPConeGeometry } from "../Utils/geometryUtils";
import { useStore } from "../Store/zustandStore";

export default function BoxFan({ frame, selected }) {
  const [frameThickness] = useStore((state) => [fromMM(state.frameThickness)]);

  const frameLength = fromMM(frame.length);
  const frameHeight = fromMM(frame.height);
  const frameWidth = fromMM(frame.width);

  // BoxFan body dimensions
  const boxHeight = fromMM(frame.fan?.boxHeight ?? 900);
  const boxWidth = fromMM(frame.fan?.boxWidth ?? 1000);
  const boxLength = fromMM(frame.fan?.boxLength ?? 1000);
  const footSize = fromMM(100);
  const cornerOffset = fromMM(20);
  const cutoutSize = fromMM(300);
  const cutoutOffsetFromTop = boxHeight / 2 - cutoutSize / 2; // centers cutout vertically on the AHU-facing face
  const grillBarThickness = fromMM(2);
  const cutoutGrillBarWidth = fromMM(100);
  const frontBackGrillSpacing = fromMM(150);
  const frontBackGrillBarWidth = fromMM(500);
  const frontBackGrillBarLength = fromMM(50);

  // The exterior fan frame sits at the RIGHT end of the unit, so the AHU body
  // ends at this frame's left edge. The cone spans the gap from that wall to
  // the box's cutout face, and the box extends rightward, filling the frame
  // (backend sizes frame.length = coneGap + boxLength, like the exterior ESP).
  const gap = fromMM(frame.fan?.coneGap ?? 500);
  const ahuLeftWallX = -frameLength / 2;
  const boxCutoutFaceX = ahuLeftWallX + gap;
  const boxCenterX = boxCutoutFaceX + boxLength / 2;
  const boxCenterY = 0;
  const boxCenterZ = 0;

  // Cutout center on the right face (in world-frame coords)
  const cutoutCenterY = boxCenterY + boxHeight / 2 - cutoutOffsetFromTop - cutoutSize / 2;
  const cutoutCenterZ = boxCenterZ;

  // Cone from the AHU wall (full frame opening) to the box's cutout face
  const coneGeom = useMemo(() => {
    return buildESPConeGeometry([
      // top
      [
        new THREE.Vector3(ahuLeftWallX, frameHeight / 2, -frameWidth / 2),
        frameWidth,
        0,
        new THREE.Vector3(boxCutoutFaceX, cutoutCenterY + cutoutSize / 2, cutoutCenterZ - cutoutSize / 2),
        cutoutSize,
        0,
      ],
      // bottom
      [
        new THREE.Vector3(ahuLeftWallX, -frameHeight / 2, -frameWidth / 2),
        frameWidth,
        0,
        new THREE.Vector3(boxCutoutFaceX, cutoutCenterY - cutoutSize / 2, cutoutCenterZ - cutoutSize / 2),
        cutoutSize,
        0,
      ],
      // back
      [
        new THREE.Vector3(ahuLeftWallX, frameHeight / 2, -frameWidth / 2),
        0,
        -frameHeight,
        new THREE.Vector3(boxCutoutFaceX, cutoutCenterY + cutoutSize / 2, cutoutCenterZ - cutoutSize / 2),
        0,
        -cutoutSize,
      ],
      // front
      [
        new THREE.Vector3(ahuLeftWallX, frameHeight / 2, frameWidth / 2),
        0,
        -frameHeight,
        new THREE.Vector3(boxCutoutFaceX, cutoutCenterY + cutoutSize / 2, cutoutCenterZ + cutoutSize / 2),
        0,
        -cutoutSize,
      ],
    ]);
  }, [frameLength, frameHeight, frameWidth, boxHeight, boxLength, cutoutSize, gap]);

  // Cutout shape (box-local coords for the right face)
  const cutoutShape = useMemo(() => {
    const shape = new THREE.Shape();
    shape.moveTo(-boxWidth / 2, -boxHeight / 2);
    shape.lineTo(boxWidth / 2, -boxHeight / 2);
    shape.lineTo(boxWidth / 2, boxHeight / 2);
    shape.lineTo(-boxWidth / 2, boxHeight / 2);
    shape.lineTo(-boxWidth / 2, -boxHeight / 2);

    const hole = new THREE.Path();
    const half = cutoutSize / 2;
    const yc = boxHeight / 2 - cutoutOffsetFromTop - cutoutSize / 2;
    hole.moveTo(-half, yc - half);
    hole.lineTo(half, yc - half);
    hole.lineTo(half, yc + half);
    hole.lineTo(-half, yc + half);
    hole.lineTo(-half, yc - half);
    shape.holes.push(hole);
    return shape;
  }, [boxWidth, boxHeight, cutoutSize, cutoutOffsetFromTop]);

  // Box materials. BoxGeometry face order: [+X, -X, +Y, -Y, +Z, -Z]. -X is the AHU-facing (cutout) side.
  const boxMaterials = useMemo(
    () => [
      new THREE.MeshStandardMaterial({ color: selected ? "Orange" : "DarkGrey", side: THREE.DoubleSide }),
      new THREE.MeshStandardMaterial({ color: selected ? "Orange" : "DarkGrey", side: THREE.DoubleSide, transparent: true, opacity: 0 }),
      new THREE.MeshStandardMaterial({ color: selected ? "Orange" : "DarkGrey", side: THREE.DoubleSide }),
      new THREE.MeshStandardMaterial({ color: selected ? "Orange" : "DarkGrey", side: THREE.DoubleSide }),
      new THREE.MeshStandardMaterial({ color: selected ? "Orange" : "DarkGrey", side: THREE.DoubleSide }),
      new THREE.MeshStandardMaterial({ color: selected ? "Orange" : "DarkGrey", side: THREE.DoubleSide }),
    ],
    [selected],
  );

  const feetPositions = [
    [-boxLength / 2 + cornerOffset + footSize / 2, -boxHeight / 2 - footSize / 2, -boxWidth / 2 + cornerOffset + footSize / 2],
    [-boxLength / 2 + cornerOffset + footSize / 2, -boxHeight / 2 - footSize / 2, boxWidth / 2 - cornerOffset - footSize / 2],
    [boxLength / 2 - cornerOffset - footSize / 2, -boxHeight / 2 - footSize / 2, -boxWidth / 2 + cornerOffset + footSize / 2],
    [boxLength / 2 - cornerOffset - footSize / 2, -boxHeight / 2 - footSize / 2, boxWidth / 2 - cornerOffset - footSize / 2],
  ];

  const cutoutGrillBarPositions = Array.from({ length: 5 }, (_, i) => {
    const localY = boxHeight / 2 - cutoutOffsetFromTop - cutoutSize + (i + 0.5) * (cutoutSize / 5);
    return [-boxLength / 2 - 0.01, localY, 0];
  });

  const frontGrillBarPositions = Array.from({ length: 6 }, (_, i) => [0, (i - 2.5) * frontBackGrillSpacing, -boxWidth / 2 + 0.01]);
  const backGrillBarPositions = Array.from({ length: 6 }, (_, i) => [0, (i - 2.5) * frontBackGrillSpacing, boxWidth / 2 - 0.01]);

  // Flex-connector cylinders mirror PRLTCFan: three short cylinders in the cone-to-AHU gap.
  const connectorY = cutoutCenterY;
  const connectorZ = cutoutCenterZ;
  const c1x = ahuLeftWallX + fromMM(75) / 2;
  const c2x = ahuLeftWallX + fromMM(75) + fromMM(100) / 2;
  const c3x = ahuLeftWallX + fromMM(75) + fromMM(100) + fromMM(75) / 2;

  return (
    <group position={[0, -frameThickness, 0]} userData={{ excludeProjections: ["left"] }}>
      <mesh geometry={coneGeom}>
        <meshStandardMaterial color={"DarkGrey"} side={THREE.DoubleSide} />
        <Edges scale={1} renderOrder={1000}>
          <meshBasicMaterial transparent color="DarkGrey" widthTest={true} />
        </Edges>
      </mesh>

      <mesh position={[c1x, connectorY, connectorZ]} rotation={[0, 0, Math.PI / 2]}>
        <cylinderGeometry args={[0.25, 0.25, fromMM(75), 16]} />
        <meshStandardMaterial color={"DarkGrey"} side={THREE.DoubleSide} />
        <Edges scale={1} renderOrder={1000}>
          <meshBasicMaterial transparent color="DarkGrey" widthTest={true} />
        </Edges>
      </mesh>
      <mesh position={[c2x, connectorY, connectorZ]} rotation={[0, 0, Math.PI / 2]}>
        <cylinderGeometry args={[0.24, 0.24, fromMM(100), 16]} />
        <meshStandardMaterial color={"DarkGrey"} side={THREE.DoubleSide} />
        <Edges scale={1} renderOrder={1000}>
          <meshBasicMaterial transparent color="DarkGrey" widthTest={true} />
        </Edges>
      </mesh>
      <mesh position={[c3x, connectorY, connectorZ]} rotation={[0, 0, Math.PI / 2]}>
        <cylinderGeometry args={[0.25, 0.25, fromMM(75), 16]} />
        <meshStandardMaterial color={"DarkGrey"} side={THREE.DoubleSide} />
        <Edges scale={1} renderOrder={1000}>
          <meshBasicMaterial transparent color="DarkGrey" widthTest={true} />
        </Edges>
      </mesh>

      <group position={[boxCenterX, boxCenterY, boxCenterZ]}>
        <mesh material={boxMaterials}>
          <boxGeometry args={[boxLength, boxHeight, boxWidth]} />
          <Edges scale={1} renderOrder={1000}>
            <meshBasicMaterial transparent color="Grey" widthTest={true} />
          </Edges>
        </mesh>

        <mesh position={[-boxLength / 2 - 0.01, 0, 0]} rotation={[0, Math.PI / 2, 0]}>
          <extrudeGeometry args={[cutoutShape, { depth: grillBarThickness, bevelEnabled: false }]} />
          <meshStandardMaterial color={selected ? "Orange" : "DarkGrey"} side={THREE.DoubleSide} />
          <Edges scale={1} renderOrder={1000}>
            <meshBasicMaterial transparent color="Grey" widthTest={true} />
          </Edges>
        </mesh>

        {cutoutGrillBarPositions.map((p, i) => (
          <mesh key={`cutout-grill-${i}`} position={p} rotation={[Math.PI / 2, 0, 0]}>
            <boxGeometry args={[cutoutGrillBarWidth, cutoutSize, grillBarThickness]} />
            <meshStandardMaterial color={selected ? "Orange" : "DarkGrey"} side={THREE.DoubleSide} />
            <Edges scale={1} renderOrder={1000}>
              <meshBasicMaterial transparent color="Grey" widthTest={true} />
            </Edges>
          </mesh>
        ))}

        {frontGrillBarPositions.map((p, i) => (
          <mesh key={`front-grill-${i}`} position={p} rotation={[Math.PI / 2, 0, 0]}>
            <boxGeometry args={[frontBackGrillBarWidth, frontBackGrillBarLength, grillBarThickness]} />
            <meshStandardMaterial color={selected ? "Orange" : "DarkGrey"} side={THREE.DoubleSide} />
            <Edges scale={1} renderOrder={1000}>
              <meshBasicMaterial transparent color="Grey" widthTest={true} />
            </Edges>
          </mesh>
        ))}

        {backGrillBarPositions.map((p, i) => (
          <mesh key={`back-grill-${i}`} position={p} rotation={[Math.PI / 2, 0, 0]}>
            <boxGeometry args={[frontBackGrillBarWidth, frontBackGrillBarLength, grillBarThickness]} />
            <meshStandardMaterial color={selected ? "Orange" : "DarkGrey"} side={THREE.DoubleSide} />
            <Edges scale={1} renderOrder={1000}>
              <meshBasicMaterial transparent color="Grey" widthTest={true} />
            </Edges>
          </mesh>
        ))}

        {feetPositions.map((p, i) => (
          <mesh key={`foot-${i}`} position={p}>
            <boxGeometry args={[footSize, footSize, footSize]} />
            <meshStandardMaterial color="Black" />
            <Edges scale={1} renderOrder={1000}>
              <meshBasicMaterial transparent color="Grey" widthTest={true} />
            </Edges>
          </mesh>
        ))}
      </group>
    </group>
  );
}
